import { useCallback } from "react";

import { BTDropdown } from "@btnet/core-components";

import { BdsIconCaretSmallDown, BdsIconDownload } from "@buildertrend/components";

import { BTButton, type BTButtonType } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTMenu } from "commonComponents/btWrappers/BTMenu/BTMenu";

import { useDownloadPlanSet } from "./useDownloadPlanSet";

interface IDownloadPlanSetIconDropdownProps {
    setId: string;
    jobId: number | null;
    shareToken?: string;
    /** @default "tertiary" */
    displayType?: BTButtonType;
    /**
     * Whether the set has any annotations to download. When false, the dropdown
     * collapses to a single Download button — there's no meaningful Base vs.
     * Markups distinction. Always supplied by callers in BTNet because the field
     * is exposed on every relevant API response (PlanSet, PlanSetEntityRecord,
     * ExternalBidPlanSet, ExternalPOPlanSet).
     */
    setHasAnnotations: boolean;
    bidId?: number;
}

/**
 * Icon-only dropdown variant of the plan-set download. Used on linked plan-set
 * rows in the specs panel and in the panel viewer header.
 *
 * Dropdown menu has two options: Base (no annotations) and Markups (annotations
 * applied + flattened server-side via XFDF). The dropdown collapses to a single
 * direct-click Base download when the set has no annotations to download, since
 * there's nothing to differentiate.
 */
export function DownloadPlanSetIconDropdown({
    setId,
    jobId,
    shareToken,
    displayType = "tertiary",
    setHasAnnotations,
    bidId,
}: IDownloadPlanSetIconDropdownProps) {
    const { download, isWorking } = useDownloadPlanSet({ setId, jobId, shareToken, bidId });

    const handleBase = useCallback(() => {
        void download(false);
    }, [download]);

    const handleMarkups = useCallback(() => {
        void download(true);
    }, [download]);

    if (!setHasAnnotations) {
        return (
            <BTButton
                data-testid={`plan-set-download-${setId}`}
                type={displayType}
                icon={<BdsIconDownload />}
                disabled={isWorking}
                loading={isWorking}
                onClick={handleBase}
                aria-label="Download plan set"
            />
        );
    }

    const overlay = (
        <BTMenu testid={`plan-set-download-menu-${setId}`}>
            <BTMenu.Item
                testid={`plan-set-download-base-${setId}`}
                key="base"
                onClick={handleBase}
                aria-label="Download without annotations"
            >
                Base
            </BTMenu.Item>
            <BTMenu.Item
                testid={`plan-set-download-markups-${setId}`}
                key="markups"
                onClick={handleMarkups}
                aria-label="Download with annotations"
            >
                Markups
            </BTMenu.Item>
        </BTMenu>
    );

    return (
        <BTDropdown
            testid={`plan-set-download-dropdown-${setId}`}
            overlay={overlay}
            disabled={isWorking}
            placement="bottomRight"
        >
            <BTButton
                data-testid={`plan-set-download-${setId}`}
                type={displayType}
                disabled={isWorking}
                loading={isWorking}
                aria-label="Download plan set"
            >
                <BdsIconDownload />
                {displayType === "secondary" && <BdsIconCaretSmallDown />}
            </BTButton>
        </BTDropdown>
    );
}
